import { useRef } from "react";
import Autoplay from "embla-carousel-autoplay";
import { Briefcase, ExternalLink } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { projetos } from "@/data/portfolio";

/**
 * Carrossel de projetos desenvolvidos pela itaCondo / equipe.
 * Usado exclusivamente na página /servicos/webdesign.
 */
export const PortfolioCarousel = () => {
  const autoplay = useRef(
    Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true })
  );

  return (
    <section className="py-20 lg:py-28 bg-slate-900 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[800px] rounded-full bg-blue-500/5 blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-6">
            <Briefcase className="h-4 w-4 text-blue-400" />
            <span className="text-sm text-blue-300">Portfólio</span>
          </div>

          <h2 className="text-3xl lg:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
              Projetos desenvolvidos
            </span>
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Empresas que já confiam no nosso trabalho — de SaaS a sites
            institucionais, entregamos resultado.
          </p>
        </div>

        <Carousel
          opts={{ align: "start", loop: true }}
          plugins={[autoplay.current]}
          className="w-full"
        >
          <CarouselContent className="-ml-4">
            {projetos.map((p) => (
              <CarouselItem
                key={p.nome}
                className="pl-4 md:basis-1/2 lg:basis-1/3"
              >
                <Card className="group bg-slate-800/50 border-white/10 backdrop-blur-sm hover:border-blue-500/40 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300 h-full overflow-hidden">
                  <CardContent className="p-0 flex flex-col h-full">
                    {/* Browser mockup */}
                    <div className="bg-slate-900/80 border-b border-white/10">
                      <div className="flex items-center gap-1.5 px-3 py-2">
                        <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
                        <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/70" />
                        <span className="h-2.5 w-2.5 rounded-full bg-green-500/70" />
                        <div className="ml-3 flex-1 min-w-0 truncate text-[10px] text-slate-500">
                          {p.nome.toLowerCase().replace(/\s+/g, "")}.com
                        </div>
                      </div>
                    </div>

                    <div className="relative aspect-[16/10] overflow-hidden bg-slate-800">
                      <img
                        src={p.imagem}
                        alt={`Projeto ${p.nome} - ${p.segmento}`}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>

                    <div className="p-5 flex flex-col gap-2 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-lg font-semibold text-white truncate">
                          {p.nome}
                        </h3>
                        <ExternalLink className="h-4 w-4 text-blue-400 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <span className="inline-flex w-fit items-center text-[11px] uppercase tracking-wider font-medium text-blue-300 bg-blue-500/10 border border-blue-500/20 px-2 py-0.5 rounded-full">
                        {p.segmento}
                      </span>
                      <p className="text-sm text-slate-400 leading-relaxed mt-1">
                        {p.descricao}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>

          <CarouselPrevious className="hidden sm:flex -left-4 lg:-left-12 bg-slate-800 border-white/10 text-blue-300 hover:bg-blue-500/20 hover:text-white" />
          <CarouselNext className="hidden sm:flex -right-4 lg:-right-12 bg-slate-800 border-white/10 text-blue-300 hover:bg-blue-500/20 hover:text-white" />
        </Carousel>

        {/* Quem confia em nós */}
        <div className="mt-16 pt-10 border-t border-white/10">
          <p className="text-center text-xs uppercase tracking-[0.2em] text-slate-500 mb-6">
            Quem confia em nós
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            {projetos.map((p) => (
              <span
                key={p.nome}
                className="text-sm font-semibold text-slate-500 hover:text-blue-300 transition-colors"
              >
                {p.nome}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PortfolioCarousel;